import { Button } from "@/components/common/Button";
import { postAuthWallet, putAuthWallet } from "../api/user/connect-wallet";
import { useAccount, useChainId, useDisconnect, useSignMessage } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useEffect, useState } from "react";
import { logger } from "@/shared/utils/logger";
import { createSiweMessage } from "viem/siwe";
import { useAuthStore } from "../store/authStore";
import { formatEllipsis } from "../utils/format";

type Props = {
  setConnected: (value: boolean) => void;
};

export const WalletConnect = ({ setConnected }: Props) => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { openConnectModal } = useConnectModal();
  const { disconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);

  const [siweMessage, setSiweMessage] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [isCooldown, setIsCooldown] = useState(false);

  useEffect(() => {
    setConnected(isConnected);
    setSignature(null);
    setSiweMessage(null);
  }, [isConnected, setConnected]);

  const handleSignature = async () => {
    if (isCooldown) return;

    if (!isConnected || !address) {
      openConnectModal?.();
      return;
    }

    try {
      setIsCooldown(true);

      const timer = setTimeout(() => {
        setIsCooldown(false);
      }, 5000);

      const dataWallet = await postAuthWallet({
        wallet: address,
        network: "EVM",
        chainId: chainId.toString(),
      });

      if (!dataWallet) {
        disconnect();
        return;
      }

      const message = createSiweMessage({
        address,
        chainId,
        domain: window.location.host,
        uri: window.location.origin,
        version: "1",
        nonce: dataWallet.nonce,
      });

      const sig = await signMessageAsync({ message });

      setSiweMessage(message);
      setSignature(sig);

      return () => clearTimeout(timer);
    } catch (error) {
      logger.error(error);
      setIsCooldown(false);
    }
  };

  const handleAuth = async () => {
    if (!signature || !siweMessage || !address) return;

    const res = await putAuthWallet({
      wallet: address,
      network: "EVM",
      chainId: chainId.toString(),
      message: siweMessage,
      signature: signature,
    });

    if (res?.accessToken && res?.refreshToken) {
      setAccessToken(res.accessToken);

      window.location.reload()
    }
  };

  const handleDisconnect = async () => {
    disconnect();
  };

  return (
    <>
      {isConnected && (
        <>
          <div className="w-full flex justify-start">
            <Button onClick={handleDisconnect} mode="bezeled" text="Back" />
          </div>

          <p className="text-[1.0625rem] leading-[1.375rem] font-semibold text-center">
            Sign in with Wallet
          </p>

          {address && (
            <p className="text-[0.8125rem] leading-[1rem] text-section-header-text tracking-tighter uppercase">
              Current Wallet Address: {formatEllipsis(address)}
            </p>
          )}
        </>
      )}
      <div className="w-full flex items-center gap-4 flex-wrap">
        {!signature ? (
          <Button
            onClick={handleSignature}
            disabled={isCooldown}
            loading={isCooldown}
            text={
              isConnected
                ? "Continue and confirm in your wallet app"
                : "Connect with wallet"
            }
            className="w-full"
          />
        ) : (
          <Button onClick={handleAuth} text={"Login"} className="w-full" />
        )}
      </div>
    </>
  );
};
